<script setup lang="ts">
import {computed} from "vue";
import {UserRound} from "@lucide/vue";
import userMaleIcon from "@/assets/icons/user-male.png";
import userFemaleIcon from "@/assets/icons/user-female.png";
import dateHelper from "@/core/helpers/dateHelper";
import useCustomer from "@/customer/composables/useCustomer";

/**
 * The card that names the patient beside the fil.
 *
 * It is the standing context of the entretien: the pharmacist speaks to
 * someone for the whole course, and the column says who without them having
 * to scroll back up. Nothing here is matter of the entretien — no served
 * text, no action, no completion — so it reads the patient record rather
 * than a bloc: the identity and the pathologies of a patient exist outside
 * the bilan and outlive it, and carrying them on the state of the cycle
 * would copy a record that does not move while the course runs.
 *
 * The read goes out on the same connection the rest of the BPM uses, the
 * customer resource being bound to the HDS client.
 */
const props = defineProps<{customerId: number}>();

/**
 * The patient record, read through the shared cache.
 *
 * Coming from the fiche patient the record is already in hand and no request
 * goes out; opened straight from its URL — the course runs on a blank layout
 * and is addressable on its own — the composable fetches it, where the
 * patient store would have stayed empty.
 */
const {data: customer} = useCustomer(props.customerId);

const information = computed(() => customer.value?.information ?? null);

/**
 * The avatar of the patient, or none when the record does not tell.
 *
 * A patient whose gender is unknown is drawn with the neutral icon rather
 * than with a default one: guessing would put a face on a blank.
 */
const avatar = computed(() => {
    switch (Number(information.value?.gender)) {
        case 1:
            return userMaleIcon;
        case 2:
            return userFemaleIcon;
        default:
            return null;
    }
});

/**
 * The line under the name: how old the patient is, and when they were born.
 */
const birth = computed(() => {
    if (information.value === null || !information.value.birth_date) {
        return null;
    }

    return [
        dateHelper.age(information.value.birth_date),
        information.value.formatted_birth_date
            ? `né le ${information.value.formatted_birth_date}`
            : null
    ].filter(Boolean).join(' · ');
});

/**
 * The pathologies of the patient, of the Vidal referential.
 *
 * The record carries three lists — the Apodis referential, the Vidal
 * entries, and the situations read from the purchase history. The Vidal
 * ones are taken because they are what the BPM handles everywhere else, so
 * the column and the scène of the fil name the same pathologies.
 */
const pathologies = computed(() => customer.value?.vidal_pathologies ?? []);
</script>

<template>
  <div v-if="customer" class="bg-white rounded-md shadow-custom px-3.5 py-3">
    <div class="flex items-center gap-3">
      <img
        v-if="avatar"
        class="size-12 shrink-0 rounded-full"
        :src="avatar"
        alt=""
      >
      <span
        v-else
        class="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary-lightest"
      >
        <UserRound class="size-6 text-primary" :stroke-width="2.2" />
      </span>

      <span class="flex min-w-0 flex-col gap-px">
        <span class="truncate text-sm font-semibold" :title="information?.name">
          {{ information?.name }}
        </span>
        <span v-if="birth" class="truncate text-[11px] text-muted-foreground" :title="birth">
          {{ birth }}
        </span>
      </span>
    </div>

    <div v-if="pathologies.length" class="mt-2.5 flex flex-wrap gap-1.5">
      <span
        v-for="pathology in pathologies"
        :key="pathology.vidal_id"
        class="flex max-w-full items-center gap-1.5 rounded-full bg-primary-lightest
          px-2 py-0.5 text-[11px] font-medium text-gray-dark"
        :title="pathology.title"
      >
        <span class="size-1.5 shrink-0 rounded-full bg-primary" />
        <span class="truncate">{{ pathology.title }}</span>
      </span>
    </div>
  </div>
</template>
